import { FC, useCallback, useMemo, useState } from 'react';
import {
  ActionButton,
  checkLockOnPage,
  ClassifierMappingsDtoSchema,
  ClassifierMappingsFormDataType,
  ClassifierMappingsType,
  ClassifierMappingsValidFormDataSchema,
  confirmationHeaders,
  DetailLayout,
  EditLock,
  Form,
  FormAlerts,
  FormSubmitButton,
  FormSubmitHandler,
  GetApiErrorMessage,
  InfoIconUrl,
  ObjectHeader,
  ObjectType,
  Padding,
  useClassifierMappingMutation,
  useMutationErrors,
  useNavigationBlockers,
  useObjectLockQuery,
  useAlertContext,
  ValidationMessagesMap,
} from '@atlas-frontend/sdk';
import { useParams } from 'react-router-dom';
import { path } from '../../paths';
import { useNavigateTo } from '../../paths/useNavigateTo';
import { ClassifierMappingBreadcrumbs } from './ClassifierMappingBreadcrumbs';
import { ClassifierMappingFields } from './ClassifierMappingFields';
import { useWatch } from 'react-hook-form';
import { CategoryMappingTable } from './CategoryMappingTable';
import translations from './ClassifierMappingMutate.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { validateMapping } from './utils';
import {
  ClassifierMappingContextProvider,
  useClassifierMappingContext,
} from './ClassifierMappingContext';
import { ClassifierMappingExcelActions } from './excel/ClassifierMappingExcelActions';

const { addTranslations, useTranslation, t } = i18nNsFactory(
  'classifier-mapping-mutate',
);
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t(`alert.error.code.${code}`, meta?.args && { ...meta.args });

const ClassifierMappingHeader = () => {
  const code = useWatch({ name: 'code' });
  const name = useWatch({ name: 'name' });

  return (
    <Padding>
      <ObjectHeader upperText={code} lowerText={name} />
    </Padding>
  );
};

export interface ClassifierMappingMutateProps {
  mode: 'edit' | 'clone' | 'create';
  data: Omit<ClassifierMappingsFormDataType, 'classifierFrom' | 'classifierTo'>;
  readOnly?: boolean;
  onSuccess?: () => void;
}

export const ClassifierMappingMutate: FC<ClassifierMappingMutateProps> = (
  props,
) => (
  <ClassifierMappingContextProvider>
    <ClassifierMappingFormInContext {...props} />
  </ClassifierMappingContextProvider>
);

const ClassifierMappingFormInContext: FC<ClassifierMappingMutateProps> = ({
  mode,
  data: defaultValues,
  readOnly,
  onSuccess,
}) => {
  const params = useParams();
  const { clear: clearBlockers } = useNavigationBlockers();
  const id = params.id ? Number(params.id) : undefined;
  const navigateToClassifierMappingView = useNavigateTo(
    path.classifierMappingView,
    { id: id || '' },
  );
  const navigateToClassifierMappingEdit = useNavigateTo(
    path.classifierMappingEdit,
    { id: id || '' },
  );
  const { resetAlert, setSuccess } = useAlertContext();
  const [preSaveErrors, setPreSaveErrors] = useState<ValidationMessagesMap>(
    new ValidationMessagesMap(),
  );
  const { mutateAsync } = useClassifierMappingMutation();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const {
    mutationErrors: uploadErrors,
    setMutationErrors: setUploadErrors,
    clearMutationErrors: clearUploadErrors,
  } = useMutationErrors(translateMutationError);

  const { t } = useTranslation();
  const { classifierFrom, classifierTo } = useClassifierMappingContext();
  const { data: lockData } = useObjectLockQuery({
    objectId: id ?? 0,
    objectType: ObjectType.ClassifierMapping,
  });

  const clearErrors = useCallback(() => {
    clearMutationErrors();
    clearUploadErrors();
  }, [clearMutationErrors, clearUploadErrors]);

  const handleSave = useCallback(
    (data: ClassifierMappingsType, confirmed: boolean) => {
      const dto = ClassifierMappingsDtoSchema.parse(data);
      resetAlert();
      clearErrors();
      setPreSaveErrors(new ValidationMessagesMap());
      return mutateAsync({
        id: dto.id ?? undefined,
        data: dto,
        headers: confirmationHeaders(confirmed),
      })
        .then((response) => {
          if (mode === 'create')
            navigateToClassifierMappingEdit(
              response?.id ? { id: response.id } : undefined,
              {
                state: { noBlocker: true },
              },
            );

          setSuccess(mode === 'create' ? t('alert.success.create') : undefined);
          onSuccess?.();
          clearBlockers();
        })
        .catch((error) => {
          setMutationErrors(error.response?.data.errors ?? []);
          throw error;
        });
    },
    [
      resetAlert,
      clearErrors,
      mutateAsync,
      mode,
      navigateToClassifierMappingEdit,
      setSuccess,
      t,
      onSuccess,
      clearBlockers,
      setMutationErrors,
    ],
  );
  const confirmSave: FormSubmitHandler = useCallback(
    (data, confirmed) => {
      if (!confirmed) {
        const mappingViolations = validateMapping(
          data,
          t,
          classifierFrom,
          classifierTo,
        );
        if (mappingViolations.size > 0) {
          clearErrors();
          setPreSaveErrors(mappingViolations);
          return;
        }
      }
      return handleSave(data, confirmed);
    },
    [classifierFrom, classifierTo, clearErrors, handleSave, t],
  );

  const serverErrors = useMemo(
    () =>
      new ValidationMessagesMap().merge(preSaveErrors).merge(mutationErrors),
    [preSaveErrors, mutationErrors],
  );

  if (mode === 'edit') {
    checkLockOnPage(lockData, navigateToClassifierMappingView);
  }

  return (
    <Form
      schema={ClassifierMappingsValidFormDataSchema}
      defaultValues={defaultValues}
      serverErrors={serverErrors}
      onSubmit={confirmSave}
    >
      <DetailLayout
        infoIconUrl={InfoIconUrl.CLASSIFIER_MAPPING_DETAIL}
        breadCrumb={
          <ClassifierMappingBreadcrumbs
            data={{ classifierMapping: defaultValues }}
          />
        }
        headerConfig={{
          actions: (
            <>
              <ClassifierMappingExcelActions
                readOnly={readOnly}
                edit={true}
                id={id}
                onError={setUploadErrors}
                onSuccess={clearUploadErrors}
              />
              <ActionButton
                type="abort"
                onClick={() =>
                  navigateToClassifierMappingView(
                    { id: id || '' },
                    { state: { saved: 'cancel', noBlocker: false } },
                  )
                }
              />
              <FormSubmitButton>{t('button.save')}</FormSubmitButton>
            </>
          ),
        }}
        side={{
          header: <ClassifierMappingHeader />,
          content: <ClassifierMappingFields readOnly={readOnly} />,
          footer: defaultValues?.id ? (
            <EditLock
              id={defaultValues.id ?? undefined}
              entity={{
                ...defaultValues,
                id: defaultValues.id === null ? undefined : defaultValues.id,
              }}
              objectType={ObjectType.ClassifierMapping}
              onTimeout={navigateToClassifierMappingView}
            />
          ) : undefined,
        }}
        alert={
          <FormAlerts
            additionalErrors={[
              {
                errorAction: t('title.upload'),
                renderErrorHeading: renderExcelField,
                errors: uploadErrors,
              },
            ]}
          />
        }
      >
        <Padding>
          <CategoryMappingTable />
        </Padding>
      </DetailLayout>
    </Form>
  );
};

function renderExcelField(name: string) {
  const path = name.split('.');

  if (path.length === 1) {
    return t(`field.${name}.label`);
  }
  if (path.length === 3) {
    const [field, index, subField] = path;
    const rowNo = Number.parseInt(index);
    if (!Number.isNaN(rowNo)) {
      return [
        t(`field.${field}.label`),
        rowNo + 2, // 1-starting and excel header column
        t(`field.${field}.${subField}`),
      ].join(' / ');
    }
  }
  return name;
}
